import { StudentPage } from '@course/components/student-page';
import { rpx } from 'client/lib/rpx-client';
import { indexBy } from 'shared/utils';
import { Calendar, CalendarDay } from '@course/components/calendar';
import { Button } from '@components/buttons';
import dayjs from 'dayjs';
import { URLS } from 'shared/urls';
import { timeWithoutZeroes, toLocalDate, toHumanTime } from 'shared/dateutil';
import { LoadedProps, RouteLoadProps } from '@components/router';
import { defStudentRoute } from '@course/components/courses-app-router';
import { useState, useRef, useEffect } from 'preact/hooks';
import { getMeetingStatus } from 'shared/meeting-utils';
import { useIntl } from 'shared/intl/use-intl';
import { minutesToTimeString } from 'shared/dateutil';

type PageData = Awaited<ReturnType<typeof load>>;
type Item = PageData['items'][0];

type HoverState = {
  item: Item;
  rect: DOMRect;
};

export const route = defStudentRoute({
  Page,
  authLevel: 'student',
  load,
});

function CellItem({
  item,
  course,
  onHoverStart,
  onHoverEnd,
}: {
  item: Item;
  course: PageData['course'];
  onHoverStart: (item: Item, el: HTMLElement) => void;
  onHoverEnd: () => void;
}) {
  return (
    <Button
      class="relative w-full flex text-left rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-800 p-1 -ml-1 box-content text-gray-500"
      data-tooltip={item.type === 'module' ? item.title : undefined}
      onMouseEnter={
        item.type === 'meeting'
          ? (e: MouseEvent) => onHoverStart(item, e.currentTarget as HTMLElement)
          : undefined
      }
      onMouseLeave={item.type === 'meeting' ? onHoverEnd : undefined}
      href={
        item.type === 'meeting'
          ? URLS.student.meeting({
              course,
              meeting: item,
            })
          : URLS.student.module({
              courseId: course.id,
              moduleId: item.id,
            })
      }
    >
      <span class="inline-block items-center">
        <span
          class={`w-2 h-2 inline-block rounded-full ${
            item.type === 'meeting' ? 'bg-indigo-300' : 'bg-blue-500'
          }`}
        ></span>
      </span>
      <time class="opacity-75 mx-0.5 text-xs lg:mx-1.5">{timeWithoutZeroes(item.date)}</time>
      <span class="hidden md:inline overflow-hidden whitespace-nowrap font-semibold">
        {item.title}
      </span>
    </Button>
  );
}

function MeetingHoverCard({
  item,
  rect,
  course,
  onMouseEnter,
  onMouseLeave,
}: {
  item: Item;
  rect: DOMRect;
  course: PageData['course'];
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) {
  const intl = useIntl();
  const cardRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: rect.top, left: rect.right + 8 });

  useEffect(() => {
    const el = cardRef.current;
    if (!el) {
      return;
    }
    const cardRect = el.getBoundingClientRect();
    let top = rect.top;
    let left = rect.right + 8;

    if (left + cardRect.width > window.innerWidth - 8) {
      left = rect.left - cardRect.width - 8;
    }
    if (left < 8) {
      left = 8;
    }
    if (top + cardRect.height > window.innerHeight - 8) {
      top = window.innerHeight - cardRect.height - 8;
    }
    if (top < 8) {
      top = 8;
    }
    setPos({ top, left });
  }, [rect]);

  const status = getMeetingStatus({
    scheduledAt: item.date,
    durationMinutes: item.durationMinutes || 60,
  });
  const isLive = status === 'inProgress' || status === 'aboutToStart';

  const descriptionText = item.description
    ?.replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120);

  const meetingUrl = URLS.student.meeting({ course, meeting: item });

  return (
    <div
      ref={cardRef}
      class="fixed z-50 w-72 rounded-xl p-4 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg an-fade-in"
      style={{ top: `${pos.top}px`, left: `${pos.left}px` }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {isLive && (
        <span class="inline-flex items-center gap-1.5 text-xs font-semibold text-red-600 uppercase tracking-wider mb-2">
          <span class="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
          Live Now
        </span>
      )}
      <p class="text-sm font-semibold text-gray-900 dark:text-white mb-1">{item.title}</p>
      <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">{toHumanTime(item.date)}</p>
      {item.durationMinutes && (
        <p class="text-xs text-gray-500 dark:text-gray-400 mb-2">
          {minutesToTimeString(item.durationMinutes, intl)}
        </p>
      )}
      {descriptionText && (
        <p class="text-xs text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
          {descriptionText}
          {descriptionText.length >= 120 ? '...' : ''}
        </p>
      )}
      <a
        href={meetingUrl}
        class="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 no-underline"
      >
        View details
        <svg
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="2.5"
          stroke="currentColor"
          style="width: 12px; height: 12px"
        >
          <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
        </svg>
      </a>
    </div>
  );
}

function Page(props: LoadedProps<typeof load>) {
  const routeDate = toLocalDate(props.params.date);
  const { course, items } = props.state;
  const [hovered, setHovered] = useState<HoverState | undefined>(undefined);
  const hoverTimerRef = useRef<number>(0);

  useEffect(() => {
    function onScroll() {
      setHovered(undefined);
    }
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, []);

  function handleHoverStart(item: Item, el: HTMLElement) {
    clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = window.setTimeout(() => {
      setHovered({ item, rect: el.getBoundingClientRect() });
    }, 200);
  }

  function handleHoverEnd() {
    clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = window.setTimeout(() => {
      setHovered(undefined);
    }, 100);
  }

  function handleCardEnter() {
    clearTimeout(hoverTimerRef.current);
  }

  function handleCardLeave() {
    clearTimeout(hoverTimerRef.current);
    setHovered(undefined);
  }

  return (
    <StudentPage
      p="py-0"
      course={course}
      currentLink="calendar"
      editLink={{
        url: URLS.guide.coursePage({
          courseId: course.id,
          page: 'calendar',
        }),
      }}
    >
      <div class="w-full xl:w-5xl max-w-5xl p-8 pb-24 mx-auto">
        <Calendar
          items={items}
          currentDate={routeDate}
          isReadOnly
          renderCellItems={({ items, date, now }) => (
            <div class="min-h-12 lg:min-h-24">
              <CalendarDay date={date} now={now} />
              {items.map((item) => (
                <CellItem
                  key={item.key}
                  item={item}
                  course={course}
                  onHoverStart={handleHoverStart}
                  onHoverEnd={handleHoverEnd}
                />
              ))}
            </div>
          )}
          onChange={(newDate) =>
            props.router.rewrite(
              URLS.student.coursePage({
                course,
                page: `calendar?date=${dayjs(newDate).format('YYYY-MM-DD')}`,
              }),
            )
          }
        />
      </div>
      {hovered && hovered.item.type === 'meeting' && (
        <MeetingHoverCard
          item={hovered.item}
          rect={hovered.rect}
          course={course}
          onMouseEnter={handleCardEnter}
          onMouseLeave={handleCardLeave}
        />
      )}
    </StudentPage>
  );
}

async function load(route: RouteLoadProps) {
  const routeParams = route.params;
  const courseId = routeParams.courseId;
  const [course, rawItems] = await Promise.all([
    rpx.courses.getStudentCourse({ id: courseId }),
    rpx.courses.getScheduledItemsForStudent({
      courseId,
    }),
  ]);

  return {
    course,
    items: Object.values(
      indexBy(
        (x) => x.key,
        rawItems.map((x) => ({
          ...x,
          key: `${x.type}-${x.id}`,
          date: new Date(x.date),
        })),
      ),
    ),
  };
}
